import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import type { RouteLocation } from 'vue-router'
import { useRoute } from 'vue-router'
import { debounce, silentCloned, silentParse } from '@/utils/helper'

type Tab = Pick<RouteLocation, 'name' | 'path' | 'query' | 'meta'>

export const useTabStore = defineStore('tab', () => {
  const localTabsData = process.browser ? localStorage.getItem('_tabs') : null

  const localTabs = localTabsData ? silentParse(localTabsData) : []

  const tabs = ref<Tab[]>(localTabs)

  const currentTabIndex = ref(0)

  const currentTab = computed(() => tabs.value[currentTabIndex.value])

  const route = useRoute()

  const save = debounce(() => {
    localStorage.setItem('_tabs', JSON.stringify(tabs.value))
  }, 500)

  watch(tabs.value, (newVal, oldVal) => {
    if (newVal)
      save()
  })

  watch(
    route,
    (newVal, oldVal) => {
      // console.log('Route Change', { oldVal, newVal })
      if (
        !newVal?.path
        || newVal?.path === '/'
        || newVal.meta?.hideInTab)
        return

      const oldIndex = tabs.value.findIndex(t => t.path === oldVal?.path)
      const index = tabs.value.findIndex(t => t.path === newVal.path)
      if (index !== -1) {
        // 如果当前tab是当前tab，则更新
        if (oldVal?.path === newVal.path) {
          const { name, path, meta, query } = newVal
          const cloned = { name, meta, path, query }
          if (oldIndex !== -1)
            tabs.value.splice(oldIndex, 1, cloned)
        }
        // 如果当前tab是noKeepAlive，则删除
        else if (oldVal?.meta?.noKeepAlive) {
          if (oldIndex !== -1)
            tabs.value.splice(oldIndex, 1)
        }
        currentTabIndex.value = index
      }
      else {
        // 如果当前tab不是当前tab，则添加
        const { name, path, meta, query } = newVal
        const cloned = { name, meta, path, query }
        tabs.value.push(cloned)
        currentTabIndex.value = tabs.value.length - 1
      }
    },
    { immediate: true },
  )

  const removeTab = (tab: Tab) => {
    if (tabs.value.length === 1)
      return

    const index = tabs.value.findIndex(t => t.path === tab.path)
    if (index === -1)
      return

    tabs.value.splice(index, 1)
  }

  return { tabs, currentTabIndex, currentTab, save, removeTab }
})
